import { createRouter, createWebHistory } from 'vue-router';
import Home from '../views/Home.vue';

const SITE_URL = 'https://thegraphicinstaller.com';

// Builds the description + Open Graph/Twitter meta tags for a route. These
// are injected client-side on navigation (see router.beforeEach below), so
// they help Google's JS-rendering crawler and in-app browser previews, but
// NOT non-JS link-preview bots (iMessage/Slack/Discord/etc. unfurl the raw
// static public/index.html, which only has one generic set of OG tags -
// true per-page social preview images would need static prerendering).
// (og:url and the canonical link are set separately, from the route path.)
function pageMeta(title, description) {
	return [
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
	];
}

const HOME_TITLE =
	'MFD Solutions - Vehicle Wrap Installation in Middle Tennessee';
const ABOUT_TITLE =
	'About MFD Solutions - Vehicle Wrap Installer in Middle Tennessee';
const PROJECTS_TITLE =
	'Portfolio - Vehicle Wraps & Graphics Installs | MFD Solutions';
const CONTACT_TITLE = 'Contact MFD Solutions - Vehicle Wraps in Middle Tennessee';

const routes = [
	{
		path: '/',
		name: 'Home',
		component: Home,
		meta: {
			title: HOME_TITLE,
			metaTags: pageMeta(
				HOME_TITLE,
				'MFD Solutions installs vehicle wraps, chrome deletes, decals, lettering, and fleet branding across Middle Tennessee. Precision install work for cars, trucks, fleets, and storefronts.'
			),
		},
	},
	{
		path: '/about',
		name: 'About',
		// route level code-splitting
		// this generates a separate chunk (about.[hash].js) for this route
		// which is lazy-loaded when the route is visited.
		component: () =>
			import(/* webpackChunkName: "about" */ '../views/About.vue'),
		meta: {
			title: ABOUT_TITLE,
			metaTags: pageMeta(
				ABOUT_TITLE,
				"Meet MFD Solutions - nearly 20 years of hands-on vehicle wrap and graphics installation experience serving Middle Tennessee."
			),
		},
	},
	{
		path: '/projects',
		name: 'Projects',
		// route level code-splitting
		// this generates a separate chunk (projects.[hash].js) for this route
		// which is lazy-loaded when the route is visited.
		component: () =>
			import(/* webpackChunkName: "projects" */ '../views/Projects.vue'),
		meta: {
			title: PROJECTS_TITLE,
			metaTags: pageMeta(
				PROJECTS_TITLE,
				'Browse completed vehicle wrap, fleet, racecar, and storefront graphics installs by MFD Solutions in Middle Tennessee.'
			),
		},
	},
	{
		path: '/contact',
		name: 'Contact',
		// route level code-splitting
		// this generates a separate chunk (contact.[hash].js) for this route
		// which is lazy-loaded when the route is visited.
		component: () =>
			import(/* webpackChunkName: "contact" */ '../views/Contact.vue'),
		meta: {
			title: CONTACT_TITLE,
			metaTags: pageMeta(
				CONTACT_TITLE,
				'Get a quote for vehicle wrap, chrome delete, decal, or fleet graphics installation from MFD Solutions in Middle Tennessee.'
			),
		},
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: () =>
			import(/* webpackChunkName: "notfound" */ '../views/NotFound.vue'),
		meta: {
			title: 'Page Not Found - MFD Solutions',
			metaTags: [{ name: 'robots', content: 'noindex' }],
		},
	},
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
	scrollBehavior() {
		document.getElementById('app').scrollIntoView();
	},
});

export default router;

// Sets the browser tab title, per-page meta tags, and canonical URL from
// each route's meta on navigation. This runs before every route change,
// including on page load.
//
// public/index.html already ships a static set of description/OG/Twitter
// tags (for link-preview bots that don't run JS). Rather than appending
// duplicates, each tagDef below is matched by its `name`/`property` to any
// existing tag (static or previously-injected) and updated in place; only
// tags with no static counterpart (e.g. the 404 page's `robots: noindex`)
// are created/removed dynamically.
router.beforeEach((to, from, next) => {
	// This goes through the matched routes from last to first, finding the closest route with a title.
	// e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
	// `/nested`'s will be chosen.
	const nearestWithTitle = to.matched
		.slice()
		.reverse()
		.find((r) => r.meta && r.meta.title);

	// Find the nearest route element with meta tags.
	const nearestWithMeta = to.matched
		.slice()
		.reverse()
		.find((r) => r.meta && r.meta.metaTags);

	const previousNearestWithMeta = from.matched
		.slice()
		.reverse()
		.find((r) => r.meta && r.meta.metaTags);

	// If a route with a title was found, set the document (page) title to that value.
	if (nearestWithTitle) {
		document.title = nearestWithTitle.meta.title;
	} else if (previousNearestWithMeta) {
		document.title = previousNearestWithMeta.meta.title;
	}

	// Remove any previously dynamically-created tags with no static
	// counterpart (e.g. a stale `robots: noindex` from a prior 404 visit).
	Array.from(
		document.querySelectorAll('[data-vue-router-created]')
	).forEach((el) => el.remove());

	const canonicalUrl = `${SITE_URL}${to.path}`;
	let canonicalEl = document.querySelector('link[rel="canonical"]');
	if (!canonicalEl) {
		canonicalEl = document.createElement('link');
		canonicalEl.setAttribute('rel', 'canonical');
		document.head.appendChild(canonicalEl);
	}
	canonicalEl.setAttribute('href', canonicalUrl);

	const metaTags = [
		...((nearestWithMeta && nearestWithMeta.meta.metaTags) || []),
		{ property: 'og:url', content: canonicalUrl },
	];

	metaTags.forEach((tagDef) => {
		const key = tagDef.name ? 'name' : 'property';
		const value = tagDef[key];
		let el = document.querySelector(`meta[${key}="${value}"]`);
		if (el) {
			el.setAttribute('content', tagDef.content);
		} else {
			el = document.createElement('meta');
			Object.keys(tagDef).forEach((k) => el.setAttribute(k, tagDef[k]));
			el.setAttribute('data-vue-router-created', '');
			document.head.appendChild(el);
		}
	});

	next();
});
